import { MessageUiComponentResultOutcome } from "@redotech/crdb-enums/shopper-ai/message-ui-component-result-outcome";
import { MessageUiComponentStatus } from "@redotech/crdb-enums/shopper-ai/message-ui-component-status";
import { zodToEffectSchema } from "@redotech/effect-api-shared-common/zod";
import { ShippingAddressToUpdateSchema } from "@redotech/redo-agents-cards/support-agents/confirm-update-order-shipping-address";
import { Schema } from "effect";
import {
  MessageId,
  MessageUiComponentId,
  MessageUiComponentResultId,
} from "../../../../ids/definitions";
import { DateFromDb } from "../../../../schema/helpers";

/**
 * An `action` card runs its action once the customer approves it. A `display`
 * card only shows something, and collects a result per button press.
 */
export const UiComponentKind = Schema.Literal("action", "display");
export type UiComponentKind = typeof UiComponentKind.Type;

export const UiComponentType = Schema.Literal(
  "cancelOrder",
  "updateShippingAddress",
  "updateOrder",
  "return",
  "signIn",
  "products",
);
export type UiComponentType = typeof UiComponentType.Type;

export const UI_COMPONENT_KINDS = {
  cancelOrder: "action",
  updateShippingAddress: "action",
  updateOrder: "action",
  return: "action",
  signIn: "action",
  products: "display",
} as const satisfies Record<UiComponentType, UiComponentKind>;

export const UiComponentStatus = Schema.Enums(MessageUiComponentStatus);
export type UiComponentStatus = typeof UiComponentStatus.Type;

export const UiComponentResultOutcome = Schema.Enums(
  MessageUiComponentResultOutcome,
);
export type UiComponentResultOutcome = typeof UiComponentResultOutcome.Type;

/** Whether the customer approved an action card. Expiry is not a decision. */
export const UiComponentDecision = Schema.Literal(
  MessageUiComponentStatus.Approved,
  MessageUiComponentStatus.Denied,
);
export type UiComponentDecision = typeof UiComponentDecision.Type;

/** The `expired_reason` of a pending card read after its `expires_at`. */
export const UI_COMPONENT_TIMED_OUT_REASON = "timedOut";

const actionFields = { status: UiComponentStatus, expiresAt: DateFromDb };

const displayFields = {
  customerInput: Schema.Null,
  status: Schema.Null,
  expiresAt: Schema.Null,
};

/** `customerInput` is a partial of the fields the customer may edit; these cards have none. */
const NoEditableFields = Schema.Struct({});

/*
 * Inputs and results are read back to the agent as tool calls, so they keep to
 * JSON: mutable arrays, and null rather than an absent field.
 */

const OrderDisplay = Schema.Struct({ orderName: Schema.String });

const CancelOrderInput = Schema.Struct({
  orderId: Schema.String,
  cancellationReason: Schema.String,
});

const CancelOrderComponent = Schema.Struct({
  componentType: Schema.Literal("cancelOrder"),
  input: CancelOrderInput,
  display: OrderDisplay,
  customerInput: Schema.NullOr(NoEditableFields),
  ...actionFields,
});

const ShippingAddress = zodToEffectSchema(ShippingAddressToUpdateSchema);

const UpdateShippingAddressEditableFields = Schema.Struct({
  updatedShippingAddress: ShippingAddress,
});

const UpdateShippingAddressCustomerInput = Schema.partialWith(
  UpdateShippingAddressEditableFields,
  { exact: true },
);

const UpdateShippingAddressInput = Schema.Struct({
  orderId: Schema.String,
  ...UpdateShippingAddressEditableFields.fields,
});

const UpdateShippingAddressComponent = Schema.Struct({
  componentType: Schema.Literal("updateShippingAddress"),
  input: UpdateShippingAddressInput,
  display: OrderDisplay,
  customerInput: Schema.NullOr(UpdateShippingAddressCustomerInput),
  ...actionFields,
});

const UpdateOrderEditableFields = Schema.Struct({
  items: Schema.mutable(
    Schema.Array(
      Schema.Struct({
        variantId: Schema.String,
        quantity: Schema.Number,
        action: Schema.Literal("add", "remove"),
      }),
    ),
  ),
});

const UpdateOrderCustomerInput = Schema.partialWith(UpdateOrderEditableFields, {
  exact: true,
});

const UpdateOrderInput = Schema.Struct({
  orderId: Schema.String,
  ...UpdateOrderEditableFields.fields,
});

const UpdateOrderComponent = Schema.Struct({
  componentType: Schema.Literal("updateOrder"),
  input: UpdateOrderInput,
  display: OrderDisplay,
  customerInput: Schema.NullOr(UpdateOrderCustomerInput),
  ...actionFields,
});

const ReturnInput = Schema.Struct({
  orderId: Schema.String,
  quoteId: Schema.NullOr(Schema.String),
});

const ReturnComponent = Schema.Struct({
  componentType: Schema.Literal("return"),
  input: ReturnInput,
  display: OrderDisplay,
  customerInput: Schema.NullOr(NoEditableFields),
  ...actionFields,
});

const SignInInput = Schema.Struct({});

const SignInComponent = Schema.Struct({
  componentType: Schema.Literal("signIn"),
  input: SignInInput,
  display: Schema.Struct({}),
  customerInput: Schema.NullOr(NoEditableFields),
  ...actionFields,
});

const ProductsComponent = Schema.Struct({
  componentType: Schema.Literal("products"),
  input: Schema.Struct({
    productIds: Schema.mutable(Schema.Array(Schema.String)),
  }),
  display: Schema.Struct({
    products: Schema.Array(
      Schema.Struct({
        productId: Schema.String,
        title: Schema.String,
        priceText: Schema.optional(Schema.String),
        imageUrl: Schema.optional(Schema.String),
        productUrl: Schema.optional(Schema.String),
      }),
    ),
  }),
  ...displayFields,
});

/** A press on a product carousel acts on the one product pressed. */
const ProductPressInput = Schema.Struct({
  productId: Schema.String,
  variantId: Schema.NullOr(Schema.String),
});

/** What a card shows and runs, keyed on `componentType`. */
export const UiComponentContent = Schema.Union(
  CancelOrderComponent,
  UpdateShippingAddressComponent,
  UpdateOrderComponent,
  ReturnComponent,
  SignInComponent,
  ProductsComponent,
);
export type UiComponentContent = typeof UiComponentContent.Type;

export const NewUiComponent = Schema.extend(
  UiComponentContent,
  Schema.Struct({
    requestId: Schema.String,
    /** The show tool call that posted the card. */
    toolCallId: Schema.String,
  }),
);
export type NewUiComponent = typeof NewUiComponent.Type;

/** A stored card. A pending card read after `expiresAt` reads as expired. */
export const UiComponent = Schema.extend(
  NewUiComponent,
  Schema.Struct({
    id: MessageUiComponentId,
    messageId: MessageId,
    expiredReason: Schema.NullOr(Schema.String),
    decidedAt: Schema.NullOr(Schema.DateFromSelf),
    createdAt: Schema.DateFromSelf,
  }),
);
export type UiComponent = typeof UiComponent.Type;

/** The edits a customer made to a card before approving it, keyed on the card's `componentType`. */
export const UiComponentCustomerEdits = Schema.Union(
  Schema.Struct({
    componentType: Schema.Literal("updateShippingAddress"),
    customerInput: UpdateShippingAddressCustomerInput,
  }),
  Schema.Struct({
    componentType: Schema.Literal("updateOrder"),
    customerInput: UpdateOrderCustomerInput,
  }),
);
export type UiComponentCustomerEdits = typeof UiComponentCustomerEdits.Type;

/*
 * What each card's action returns when it succeeds. A first cut from the
 * action tools' outputs; each card's tool MR settles its own.
 */
const CancelOrderResult = Schema.Struct({
  refundAmount: Schema.NullOr(Schema.String),
});

const UpdateShippingAddressResult = Schema.Struct({
  updatedAddress: ShippingAddress,
});

const UpdateOrderResult = Schema.Struct({
  amountToPay: Schema.NullOr(Schema.String),
  amountToRefund: Schema.NullOr(Schema.String),
  paymentUrl: Schema.NullOr(Schema.String),
});

const ReturnResult = Schema.Struct({ returnId: Schema.String });

/** Signing in and adding to cart happen in the customer's browser and return nothing. */
const EmptyResult = Schema.Struct({});

const resultContent = <
  const ComponentType extends UiComponentType,
  Input extends Schema.Schema.Any,
  Result extends Schema.Schema.Any,
>(
  componentType: ComponentType,
  effectiveInput: Input,
  result: Result,
) =>
  Schema.Struct({
    componentType: Schema.Literal(componentType),
    effectiveInput,
    /** Null unless the action succeeded. */
    result: Schema.NullOr(result),
  });

/**
 * What a button press ran and returned, keyed on the `componentType` of the
 * card it answers. An action card runs its input merged with the customer's edits.
 */
export const UiComponentResultContent = Schema.Union(
  resultContent("cancelOrder", CancelOrderInput, CancelOrderResult),
  resultContent(
    "updateShippingAddress",
    UpdateShippingAddressInput,
    UpdateShippingAddressResult,
  ),
  resultContent("updateOrder", UpdateOrderInput, UpdateOrderResult),
  resultContent("return", ReturnInput, ReturnResult),
  resultContent("signIn", SignInInput, EmptyResult),
  resultContent("products", ProductPressInput, EmptyResult),
);
export type UiComponentResultContent = typeof UiComponentResultContent.Type;

export const NewUiComponentResult = Schema.extend(
  UiComponentResultContent,
  Schema.Struct({
    requestId: Schema.String,
    /** The button pressed: `approve`, `deny`, `addToCart`, and so on. */
    action: Schema.String,
    outcome: UiComponentResultOutcome,
    error: Schema.NullOr(Schema.String),
  }),
);
export type NewUiComponentResult = typeof NewUiComponentResult.Type;

export const UiComponentResult = Schema.extend(
  NewUiComponentResult,
  Schema.Struct({
    id: MessageUiComponentResultId,
    messageId: MessageId,
    createdAt: Schema.DateFromSelf,
  }),
);
export type UiComponentResult = typeof UiComponentResult.Type;
